import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { logout } from '../../lib/auth';
import { getDeviceMetadata, DeviceMetadata } from '../../db/services/device_service';
import { Button } from '@mes-amis/galette-design-system';

/**
 * Security Tab - Security and lock settings
 * Extracted from Settings page for tabbed interface
 */
export default function SecurityTab() {
  const [logoutConfirmation, setLogoutConfirmation] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [device, setDevice] = useState<DeviceMetadata | null>(null);

  useEffect(() => {
    let cancelled = false;
    // getDeviceMetadata resolves to null when no token is registered (cookie
    // fallback mode), so the .catch only fires on an actual DB read error —
    // typically a DatabaseClosedError if the component renders before the
    // unlock completes. Either way, leave the panel hidden.
    getDeviceMetadata()
      .then((meta) => {
        if (!cancelled) setDevice(meta);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const formatDate = (iso: string): string => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleLogout = async () => {
    if (logoutConfirmation.toLowerCase() !== 'logout and wipe device') {
      return;
    }

    setIsLoggingOut(true);
    await logout();
  };

  return (
    <div className="space-y-6">
      {/* Device registration — only shown when a token has been registered */}
      {device && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <DevicePhoneMobileIcon className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">This device</h2>
              <p className="text-sm text-gray-600">
                Mon Ami GO is registered to this device and uses local passkey unlock.
                Sign out from your organization's admin settings to revoke this device.
              </p>
            </div>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <dt className="text-gray-500">Device</dt>
              <dd className="text-gray-900">{device.label || 'Unnamed device'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Registered</dt>
              <dd className="text-gray-900">{formatDate(device.registered_at)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Access expires</dt>
              <dd className="text-gray-900">{formatDate(device.expires_at)}</dd>
            </div>
          </dl>
        </div>
      )}

      {/* Logout Section - Warning at top */}
      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-red-900 mb-2">
              Logout and Wipe Device
            </h2>
            <p className="text-sm text-red-800 mb-2">
              <strong>Warning:</strong> Logging out will permanently delete all local data from this device, including:
            </p>
            <ul className="text-sm text-red-800 space-y-1 list-disc list-inside mb-3">
              <li>All offline client data</li>
              <li>All documents</li>
              <li>All notes</li>
              <li>Authentication credentials</li>
              <li><strong>Any unsynced changes will be lost</strong></li>
            </ul>
            <p className="text-sm text-red-800">
              You will need to log in again and re-register authentication to regain access.
            </p>
          </div>
        </div>

        <div className="border-t border-red-300 pt-4">
          <label htmlFor="logout-confirmation" className="block text-sm font-medium text-red-900 mb-2">
            Type <code className="bg-red-100 px-2 py-0.5 rounded font-mono text-red-900">logout and wipe device</code> to confirm:
          </label>
          <div className="flex gap-3">
            <input
              id="logout-confirmation"
              type="text"
              value={logoutConfirmation}
              onChange={(e) => setLogoutConfirmation(e.target.value)}
              placeholder="Type 'logout and wipe device' to confirm"
              disabled={isLoggingOut}
              className="flex-1 px-4 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <Button
              variant="primary"
              destructive
              onClick={handleLogout}
              disabled={logoutConfirmation.toLowerCase() !== 'logout and wipe device' || isLoggingOut}
            >
              {isLoggingOut ? 'Logging out...' : 'Logout and Wipe Device'}
            </Button>
          </div>
        </div>
      </div>

    </div>
  );
}
